import React, { useEffect, useState } from "react";
import { apiErrorMessage } from "../lib/apiError";
import { useParams } from "react-router-dom";
import { useNavigate } from "../i18n/router";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../hooks/use-toast";
import {
  ArrowLeft, Loader2, Users, Check, X, Mail, Clock,
  CheckCircle2, XCircle, CreditCard, UserCheck, Filter, Bell
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_META = {
  requested:  { label: "Заявка",      color: "#92400E", bg: "#FEF3C7", icon: Clock },
  confirmed:  { label: "Подтверждено", color: "#065F46", bg: "#D1FAE5", icon: CheckCircle2 },
  paid:       { label: "Оплачено",     color: "#1E3A8A", bg: "#DBEAFE", icon: CreditCard },
  cancelled:  { label: "Отменено",     color: "#991B1B", bg: "#FEE2E2", icon: XCircle },
  attended:   { label: "Посещено",     color: "#065F46", bg: "#D1FAE5", icon: UserCheck },
  interested: { label: "Интересно",    color: "#475569", bg: "#F1F5F9", icon: Users },
};

function StatusPill({ status }) {
  const m = STATUS_META[status] || { label: status, color: "#475569", bg: "#F1F5F9", icon: Users };
  const Icon = m.icon;
  return (
    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium"
      style={{ backgroundColor: m.bg, color: m.color }} data-testid={`status-pill-${status}`}>
      <Icon className="h-3 w-3" /> {m.label}
    </span>
  );
}

function getInitials(name) {
  return (name || "?").split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 2);
}

export default function OrganizerEventAttendeesPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { toast } = useToast();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("all");
  const [acting, setActing] = useState({});
  const [sendingReminders, setSendingReminders] = useState(false);

  useEffect(() => {
    if (!authLoading && !user) navigate(`/login?redirect=/organizer/events/${id}/attendees`);
  }, [user, authLoading, id, navigate]);

  const sendReminders = async () => {
    setSendingReminders(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/events/${id}/send-reminders`, {
        method: "POST", credentials: "include",
      });
      const d = await res.json().catch(() => ({}));
      if (res.ok) {
        const sent = d.sent || 0;
        const dup = d.skipped_already || 0;
        toast({
          title: sent > 0 ? `Отправлено напоминаний: ${sent}` : "Напоминания уже отправлены",
          description: dup > 0 && sent > 0 ? `Пропущено ранее отправленных: ${dup}` : undefined,
        });
      } else if (res.status === 403) {
        toast({ title: "Доступ запрещен", variant: "destructive" });
      } else {
        toast({
          title: "Не удалось отправить",
          description: apiErrorMessage(d, "Попробуйте снова."),
          variant: "destructive",
        });
      }
    } catch (e) {
      console.error("[reminders] failed", e);
      toast({ title: "Ошибка сети", variant: "destructive" });
    } finally {
      setSendingReminders(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/organizer/events/${id}/attendees`, { credentials: "include" });
      if (res.status === 403) { setError("Доступ запрещен"); return; }
      if (!res.ok) { setError("Не удалось загрузить участников"); return; }
      setData(await res.json());
      setError(null);
    } catch {
      setError("Ошибка сети");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (user) load(); }, [user, id]);

  const updateStatus = async (userId, status) => {
    setActing(p => ({ ...p, [userId]: status }));
    try {
      const res = await fetch(`${BACKEND_URL}/api/organizer/events/${id}/attendees/${userId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ status }),
      });
      if (res.ok) {
        toast({ title: "Статус обновлен", description: STATUS_META[status]?.label });
        await load();
      } else {
        const err = await res.json().catch(() => ({}));
        toast({ title: "Ошибка", description: apiErrorMessage(err, "Не удалось обновить статус"), variant: "destructive" });
      }
    } catch {
      toast({ title: "Ошибка", description: "Сеть недоступна", variant: "destructive" });
    } finally {
      setActing(p => { const c = { ...p }; delete c[userId]; return c; });
    }
  };

  if (authLoading || !user) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin" style={{ color: "var(--eunoia-accent)" }} /></div>;
  }

  if (loading) {
    return (
      <div className="min-h-screen eunoia-space-lg px-4 pb-16">
        <div className="max-w-4xl mx-auto flex items-center justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin" style={{ color: "var(--eunoia-accent)" }} />
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen eunoia-space-lg px-4 pb-16">
        <div className="max-w-4xl mx-auto">
          <button onClick={() => navigate(-1)} className="mb-4 eunoia-caption flex items-center gap-2" style={{ color: "var(--eunoia-soft)" }}>
            <ArrowLeft className="h-4 w-4" /> Назад
          </button>
          <div className="eunoia-surface p-12 text-center">
            <p className="eunoia-body">{error || "Нет данных"}</p>
          </div>
        </div>
      </div>
    );
  }

  const attendees = data.attendees || [];
  const pending = attendees.filter(a => a.status === "requested");
  const confirmed = attendees.filter(a => ["confirmed", "paid", "attended"].includes(a.status));
  const cancelled = attendees.filter(a => a.status === "cancelled");
  const interested = attendees.filter(a => a.status === "interested");

  const filterGroups = {
    all: attendees,
    requested: pending,
    confirmed: confirmed,
    cancelled: cancelled,
    interested: interested,
  };
  const shown = filterGroups[filter] || attendees;

  return (
    <div className="min-h-screen eunoia-space-lg px-4 pb-16" data-testid="organizer-attendees-page">
      <div className="max-w-4xl mx-auto">
        <button onClick={() => navigate(`/events/${id}`)} className="mb-4 eunoia-caption flex items-center gap-2 hover:opacity-80" style={{ color: "var(--eunoia-soft)" }} data-testid="back-to-event-btn">
          <ArrowLeft className="h-4 w-4" /> К событию
        </button>

        <div className="mb-6 flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="eunoia-title-large mb-1">{data.event.title}</h1>
            <p className="eunoia-caption" style={{ color: "var(--eunoia-soft)" }}>
              {data.event.date} • {data.event.time} • {data.event.city}
              {data.event.capacity && <> • Мест: {confirmed.length} / {data.event.capacity}</>}
            </p>
          </div>
          {confirmed.length > 0 && (
            <button
              onClick={sendReminders}
              disabled={sendingReminders}
              data-testid="send-reminders-btn"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-medium border-2 transition-all hover:bg-white disabled:opacity-60"
              style={{
                borderColor: "var(--eunoia-accent)",
                color: "var(--eunoia-accent)",
                backgroundColor: "transparent",
              }}
              title="Отправить email + push-уведомление подтверждённым участникам. Повторный клик безопасен — дубликаты не отправятся."
            >
              {sendingReminders ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Bell className="h-3.5 w-3.5" />}
              Отправить напоминания
            </button>
          )}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          {[
            { key: "all",        label: "Всего",    count: attendees.length,  color: "var(--eunoia-deep)" },
            { key: "requested",  label: "Заявки",   count: pending.length,    color: "#92400E" },
            { key: "confirmed",  label: "Подтверждено", count: confirmed.length, color: "#065F46" },
            { key: "interested", label: "Интересно", count: interested.length, color: "#475569" },
          ].map(s => (
            <button key={s.key}
              onClick={() => setFilter(s.key)}
              data-testid={`stat-${s.key}`}
              className="eunoia-surface-soft p-4 text-left transition-all hover:-translate-y-0.5"
              style={{
                border: filter === s.key ? `2px solid ${s.color}` : "2px solid transparent",
              }}
            >
              <p className="text-2xl font-bold" style={{ color: s.color }}>{s.count}</p>
              <p className="text-xs mt-1" style={{ color: "var(--eunoia-soft)" }}>{s.label}</p>
            </button>
          ))}
        </div>

        {/* Filter indicator */}
        {filter !== "all" && (
          <div className="flex items-center gap-2 mb-4">
            <Filter className="h-4 w-4" style={{ color: "var(--eunoia-accent)" }} />
            <span className="eunoia-caption">Показаны: {STATUS_META[filter]?.label || filter}</span>
            <button onClick={() => setFilter("all")} className="text-xs underline" style={{ color: "var(--eunoia-accent)" }}>Сбросить</button>
          </div>
        )}

        {/* Attendees list */}
        {shown.length === 0 ? (
          <div className="eunoia-surface p-12 text-center">
            <Users className="h-12 w-12 mx-auto mb-4" style={{ color: "var(--eunoia-whisper)" }} />
            <p className="eunoia-body">
              {filter === "requested" ? "Нет новых заявок" :
               filter === "confirmed" ? "Нет подтверждённых участников" :
               "Пока нет участников"}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {shown.map(att => {
              const isActing = !!acting[att.userId];
              const isPaid = data.event.isFree === false;
              return (
                <div key={att.id || att.userId} className="eunoia-surface p-4 flex items-center gap-4 flex-wrap" data-testid={`attendee-row-${att.userId}`}>
                  <div className="w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: "var(--eunoia-accent-whisper)" }}>
                    <span className="text-sm font-semibold" style={{ color: "var(--eunoia-accent)" }}>
                      {getInitials(att.userName)}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold truncate" style={{ color: "var(--eunoia-deep)" }}>{att.userName || "Без имени"}</p>
                    <p className="text-xs truncate flex items-center gap-1" style={{ color: "var(--eunoia-soft)" }}>
                      <Mail className="h-3 w-3" /> {att.userEmail}
                    </p>
                  </div>

                  <StatusPill status={att.status} />

                  <div className="flex gap-2 flex-wrap">
                    {att.status === "requested" && (
                      <>
                        <button
                          onClick={() => updateStatus(att.userId, isPaid ? "paid" : "confirmed")}
                          disabled={isActing}
                          data-testid={`confirm-btn-${att.userId}`}
                          className="flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium text-white disabled:opacity-50 transition-all"
                          style={{ backgroundColor: "var(--eunoia-accent)" }}
                        >
                          {isActing ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
                          {isPaid ? "Оплачено" : "Подтвердить"}
                        </button>
                        <button
                          onClick={() => updateStatus(att.userId, "cancelled")}
                          disabled={isActing}
                          data-testid={`reject-btn-${att.userId}`}
                          className="flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium border disabled:opacity-50"
                          style={{ borderColor: "#DC2626", color: "#DC2626" }}
                        >
                          <X className="h-3 w-3" /> Отклонить
                        </button>
                      </>
                    )}
                    {(att.status === "confirmed" || att.status === "paid") && (
                      <>
                        <button
                          onClick={() => updateStatus(att.userId, "attended")}
                          disabled={isActing}
                          data-testid={`mark-attended-btn-${att.userId}`}
                          className="flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium border disabled:opacity-50"
                          style={{ borderColor: "var(--eunoia-accent)", color: "var(--eunoia-accent)" }}
                        >
                          <UserCheck className="h-3 w-3" /> Посетил
                        </button>
                        <button
                          onClick={() => updateStatus(att.userId, "cancelled")}
                          disabled={isActing}
                          data-testid={`cancel-btn-${att.userId}`}
                          className="flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium border disabled:opacity-50"
                          style={{ borderColor: "#DC2626", color: "#DC2626" }}
                        >
                          <X className="h-3 w-3" /> Отменить
                        </button>
                      </>
                    )}
                    {att.status === "cancelled" && (
                      <button
                        onClick={() => updateStatus(att.userId, isPaid ? "paid" : "confirmed")}
                        disabled={isActing}
                        data-testid={`restore-btn-${att.userId}`}
                        className="flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium border disabled:opacity-50"
                        style={{ borderColor: "var(--eunoia-accent)", color: "var(--eunoia-accent)" }}
                      >
                        <Check className="h-3 w-3" /> Восстановить
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
